import { differenceInCalendarDays, isFuture } from 'date-fns';
import { UserStatusType } from '../graphql/queries';
import type { UserStatus } from '../graphql/queries';
import { IDVerificationStatus, PaymentStatus } from '../models';
import { CicBackgroundReportStatus, CreditReportStatus } from '@zg-rentals/ts-capps-client';

export function getUserStatus(userStatuses: Array<UserStatus>, type: UserStatusType): UserStatus | undefined {
  return userStatuses.find((status) => status.type === type);
}

export function getExpirationDate(reportStatus?: UserStatus): Date | null {
  return reportStatus?.expirationDateTime ? new Date(reportStatus.expirationDateTime) : null;
}

export function getRequestedDate(reportStatus?: UserStatus): Date | null {
  return reportStatus?.requestedDateTime ? new Date(reportStatus.requestedDateTime) : null;
}

export function getIsPaymentValid(userStatuses: Array<UserStatus>): boolean {
  const userStatus = getUserStatus(userStatuses, UserStatusType.rentalApplicationPayment);
  const expirationDate = getExpirationDate(userStatus);
  if (!expirationDate) {
    return false;
  }
  const expirationInFuture = isFuture(expirationDate);
  return userStatus?.status === PaymentStatus.VALID && expirationInFuture;
}

export function getIsPaymentExpired(userStatuses: Array<UserStatus>): boolean {
  const userStatus = getUserStatus(userStatuses, UserStatusType.rentalApplicationPayment);
  const expirationDate = getExpirationDate(userStatus);
  if (!expirationDate) {
    return false;
  }
  const expirationExpired = !isFuture(expirationDate);
  return userStatus?.status === PaymentStatus.VALID && expirationExpired;
}

export function getPaymentExpirationDate(userStatuses: Array<UserStatus>) {
  const userStatus = getUserStatus(userStatuses, UserStatusType.rentalApplicationPayment);
  return getExpirationDate(userStatus);
}

export function getIsPaymentStepComplete(userStatuses: Array<UserStatus>, requiresTrueUp = false) {
  // A valid payment that doesn't cover this listing's price still owes a true-up
  if (requiresTrueUp) {
    return false;
  }
  // If a user cannot be found, allow them to submit the application without purchasing screening reports
  return getIsPaymentValid(userStatuses) || getIsIdentityNotFound(userStatuses);
}

export function getDaysLeftOnPayment(userStatuses: Array<UserStatus>) {
  const paymentExpirationDate = getPaymentExpirationDate(userStatuses);
  if (!paymentExpirationDate) {
    return 0;
  }
  return differenceInCalendarDays(paymentExpirationDate, new Date());
}

export function getIsIdentityVerified(userStatuses: Array<UserStatus>): boolean {
  const userStatus = getUserStatus(userStatuses, UserStatusType.idverification);
  const expirationDate = getExpirationDate(userStatus);
  if (!expirationDate) {
    return false;
  }
  const expirationInFuture = isFuture(expirationDate);
  return userStatus?.status === IDVerificationStatus.CURRENTLY_IDENTIFIED && expirationInFuture;
}

export function getIdentityExpirationDate(userStatuses: Array<UserStatus>) {
  const userStatus = getUserStatus(userStatuses, UserStatusType.idverification);
  return getExpirationDate(userStatus);
}

export function getIsIdvStepComplete(userStatuses: Array<UserStatus>): boolean {
  return getIsIdentityVerified(userStatuses) || getIsIdentityNotFound(userStatuses);
}

export function getIsBackgroundReportValid(userStatuses: Array<UserStatus>): boolean {
  const userStatus = getUserStatus(userStatuses, UserStatusType.backgroundReport);
  return (
    userStatus?.status === CicBackgroundReportStatus.AVAILABLE && getIsBackgroundReportExpirationInFuture(userStatuses)
  );
}

export function getBackgroundReportExpirationDate(userStatuses: Array<UserStatus>) {
  const userStatus = getUserStatus(userStatuses, UserStatusType.backgroundReport);
  return getExpirationDate(userStatus);
}

export function getIsBackgroundReportExpirationInFuture(userStatuses: Array<UserStatus>) {
  const expirationDate = getBackgroundReportExpirationDate(userStatuses);
  if (!expirationDate) {
    return false;
  }
  return isFuture(expirationDate);
}

export function getBackgroundReportRequestedDate(userStatuses: Array<UserStatus>) {
  const userStatus = getUserStatus(userStatuses, UserStatusType.backgroundReport);
  return getRequestedDate(userStatus);
}

export function getIsCreditReportValid(userStatuses: Array<UserStatus>): boolean {
  const userStatus = getUserStatus(userStatuses, UserStatusType.creditReport);
  return userStatus?.status === CreditReportStatus.AVAILABLE && getIsCreditReportExpirationInFuture(userStatuses);
}

export function getCreditReportExpirationDate(userStatuses: Array<UserStatus>) {
  const userStatus = getUserStatus(userStatuses, UserStatusType.creditReport);
  return getExpirationDate(userStatus);
}

export function getIsCreditReportExpirationInFuture(userStatuses: Array<UserStatus>) {
  const expirationDate = getCreditReportExpirationDate(userStatuses);
  if (!expirationDate) {
    return false;
  }
  return isFuture(expirationDate);
}

export function getCreditReportRequestedDate(userStatuses: Array<UserStatus>) {
  const userStatus = getUserStatus(userStatuses, UserStatusType.creditReport);
  return getRequestedDate(userStatus);
}

export function getIsIdentityNotFound(userStatuses: Array<UserStatus>) {
  const userStatus = getUserStatus(userStatuses, UserStatusType.idverification);
  const expirationDate = getExpirationDate(userStatus);
  if (!expirationDate) {
    return false;
  }
  const expirationInFuture = isFuture(expirationDate);
  const statusesAllowed: Array<string> = [IDVerificationStatus.THIN_FILE, IDVerificationStatus.USER_NOT_FOUND];
  return !!userStatus?.status && statusesAllowed.includes(userStatus.status) && expirationInFuture;
}

export function getIsReAuthAllowed(userStatuses: Array<UserStatus>) {
  const isPaymentValid = getIsPaymentValid(userStatuses);

  const backgroundUserStatus = getUserStatus(userStatuses, UserStatusType.backgroundReport);
  const backgroundExpirationDate = getExpirationDate(backgroundUserStatus);
  const isBackgroundReauthAllowed =
    !!backgroundExpirationDate &&
    backgroundUserStatus?.status === CicBackgroundReportStatus.NO_SSN_DOB &&
    isFuture(backgroundExpirationDate) &&
    isPaymentValid;

  const creditUserStatus = getUserStatus(userStatuses, UserStatusType.creditReport);
  const creditExpirationDate = getExpirationDate(creditUserStatus);
  const isCreditReauthAllowed =
    !!creditExpirationDate &&
    (creditUserStatus?.status === CreditReportStatus.NO_SSN ||
      creditUserStatus?.status === CreditReportStatus.LACK_OF_INFORMATION) &&
    isFuture(creditExpirationDate) &&
    isPaymentValid;

  return isBackgroundReauthAllowed || isCreditReauthAllowed;
}
